<template>
  <div
    :class="[
      'document-download-items-fi',
      subItems ? 'document-download-items-fi--sub-items' : null,
      theme ? `document-download-items-fi--${theme}` : null,
    ]"
  >
    <div
      v-for="(document, index) in documents"
      :key="`${index}-item`"
      class="document-download-items-fi__item"
    >
      <div
        :key="`${index}-item`"
        class="document-download-items-fi__item-wrapper"
      >
        <div
          class="document-download-items-fi__item-column document-download-items-fi__item-column--product"
        >
          <div class="document-download-items-fi__file-details">
            {{ document.url }}
            <a
              class="document-download-items-fi__file-link"
              :href="document.Url"
              :tabindex="isFocusable ? undefined : '-1'"
              target="_blank"
              download
              @click="handleDownload(document.Url, document.Name)"
            >
              <div class="document-download-items-fi__file-name">
                {{ document.Name }}
              </div>
            </a>
            <div class="document-download-items-fi__file-meta-info">
              <span
                v-if="isDownloadCenter && document.Language"
                class="document-download-items-fi__file-meta-info-column document-download-items-fi__file-meta-info-column--language"
              >
                <country-flag-fi
                  :country="document.Language.toLowerCase()"
                  size="small"
                />
              </span>
              <span
                v-if="document.Format || document.FileSize"
                :class="{
                  'document-download-items-fi__file-meta-info-column': true,
                  'document-download-items-fi__file-meta-info-column--has-document-number':
                    documentNumber(document),
                }"
              >
                <span class="document-download-items-fi__file-type"> {{ document.Format }}, </span>
                <span class="document-download-items-fi__file-size">
                  {{ prettyBytesFormatted(document.FileSize) }}
                </span>
              </span>
              <span
                v-if="documentNumber(document)"
                class="document-download-items-fi__file-meta-info-column"
              >
                {{ documentNumber(document) }}
              </span>
            </div>

            <div
              v-if="document.Description && isDownloadCenter"
              class="document-download-items-fi__description"
            >
              {{ document.Description }}
            </div>
          </div>
        </div>
        <a
          :class="{
            'document-download-items-fi__item-column': true,
            'document-download-items-fi__item-column--icon': true,
            'document-download-items-fi__item-column--icon-dwnld': isDownloadCenter,
          }"
          :href="document.Url"
          target="_blank"
          download
          @click="handleDownload(document.Url, document.Name)"
        >
          <icon-fi
            class="document-download-items-fi__file-download-icon-svg"
            icon="download"
          />
        </a>
        <div
          class="document-download-items-fi__item-column document-download-items-fi__item-column--details"
        >
          <div
            v-if="document.Description && !isDownloadCenter"
            class="document-download-items-fi__description"
          >
            {{ document.Description }}
          </div>
          <div
            v-if="document.categories && isDownloadCenter"
            class="document-download-items-fi__categories"
          >
            <div class="document-download-items-fi__categories__label">
              {{ $dict.get('download-Center.Document-Downloads.Label-Categories') }}
            </div>
            <div
              v-for="category in document.categories"
              :key="category.name"
            >
              <a
                v-if="category.Slug"
                :href="productUrl(category.Slug ?? '')"
                :alt="category.name"
                :tabindex="isFocusable ? undefined : '-1'"
                target="_blank"
              >
                {{ category.name }}
              </a>

              <span v-else>{{ category.name }} </span>
            </div>
          </div>
        </div>
        <div
          class="document-download-items-fi__item-column document-download-items-fi__item-column--date"
        >
          <div class="document-download-items-fi__date-container">
            <div
              v-if="document.ValidFrom && !document.ValidTo"
              class="document-download-items-fi__date-valid-from"
            >
              {{
                $dict.get('download-Center.Document-Downloads.Valid-From', [
                  getDate(document.ValidFrom) ?? '',
                ])
              }}
            </div>
            <div
              v-if="document.ValidTo && !document.ValidFrom"
              class="document-download-items-fi__date-valid-to"
            >
              {{
                $dict.get('download-Center.Document-Downloads.Valid-To', [
                  getDate(document.ValidTo) ?? '',
                ])
              }}
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="document.ValidFrom && document.ValidTo"
              class="document-download-items-fi__date-valid-to"
              v-html="
                $dict.get('download-Center.Document-Downloads.Valid-From-To', [
                  getDate(document.ValidFrom) ?? '',
                  getDate(document.ValidTo) ?? '',
                ])
              "
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
      </div>

      <document-download-items-fi
        v-if="document.Subitems"
        :key="`${index}-subitem`"
        :theme="theme"
        :documents="document.Subitems"
        sub-items
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CountryFlagFi, IconFi } from 'atoms';
import prettyBytes from 'pretty-bytes';
import { computed, onMounted, ref } from 'vue';

import { useCatalogTextList } from '@/composables/catalogTextList';
import { useEventHub } from '@/composables/eventHub';
import { useFischer } from '@/composables/fischerPlugin';

defineOptions({ name: 'DocumentDownloadItemsFi' });

const props = withDefaults(
  defineProps<{
    documents?: any[];
    subItems?: boolean;
    theme?: string | null;
    isFocusable?: boolean;
  }>(),
  {
    documents: () => [],
    subItems: false,
    theme: null,
    isFocusable: true,
  },
);

const fischer = useFischer();
const { textListValue } = useCatalogTextList();
const { sendEvent } = useEventHub();

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

const isDownloadCenter = computed(() => props.theme === 'download-center');

const prettyBytesFormatted = (bytes: number) => {
  if (!isMounted.value) {
    return '';
  }
  return prettyBytes(bytes, { maximumFractionDigits: 1 });
};

const documentNumber = (document: any) => {
  if (document.nr) {
    return document.nr;
  }

  return textListValue(document.AttributeList, 'ZULANR');
};

const getDate = (datevalue: string | null | undefined) => {
  if (!datevalue) {
    return null;
  }

  const date = new Date(datevalue);
  return date.toLocaleDateString(fischer.siteLanguage(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const productUrl = (itemSlug: string) => {
  return `${fischer.pages.products().url}${itemSlug}`;
};

const handleDownload = (url: string | undefined, name: string | undefined) => {
  if (!url || !name) {
    console.error('missing data for download event', url, name);
    return;
  }
  sendEvent('custom', {}, 'download', {
    link: url,
    name: name,
  });
};
</script>

<style scoped src="./document-download-items-fi" />
